
import {
    AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, Renderer2, ViewChild,
    ViewEncapsulation, HostListener, AfterContentChecked,
    OnDestroy
} from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { formatDate } from 'ngx-bootstrap/chronos';
import { Chart, BarElement, BarController, CategoryScale, Decimation, Filler, Legend, Title, Tooltip, registerables, plugins } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import * as ChartJs from 'chart.js';
import * as helpers from 'chart.js/helpers';
import * as _ from 'underscore';
import { ContextMenuOption } from '../../models/context-menu-option';
import { Endpoint, MessageType, UpdateStatusType, EntityType, SearchType } from '../../models/enums';
import { CellActionType, GenericColDef, GridRowSelectionType, AlignDirection } from '../../models/grid-context';
import { ReportDefinitions, ReportDisplayType, ReportSearchType, ReportType } from '../../models/report';
import { DetailsType, ReportDetailsMap } from '../../models/report-details';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { ReportsDetailsModalComponent, ReportsDetailsModalData } from '../modals/reports-details-modal/reports-details-modal.component';
import { Permissions, ObjectActionListType } from '../../models/permission';
import * as moment from 'moment';
import { Comparison } from '../../models/comparison';
import { isString } from '../../models/utils';
import { TaskStatus, TaskType } from '../../models/task';
import { MAX_LIMIT_OF_RENDERED_QUERY } from '../../models/constants';
import { Subscription } from 'rxjs';

const oneDayTicks = 1000 * 60 * 60 * 24;

@Component({
    standalone: false,
    selector: 'res-report-panel',
    templateUrl: './report-panel.component.html',
    styleUrls: ['./report-panel.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ReportPanelComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
    @HostBinding('class') classes;
    @Input() report: any;
    @Input() allReports: Array<any>;
    @Input() automationId: string;
    @Input() handle: string;
    @Output() downloadReport = new EventEmitter<any>();
    @ViewChild('leftSection') leftSection: ElementRef;
    @ViewChild('rightSection') rightSection: ElementRef;
    @ViewChild('reportGrid') reportGrid: GenericGridComponent;
    @Input() workflow: any;
    private entityChangedEvent: Subscription;
    isBusyMessage = 'Loading ...';
    selectBulkSearchTreeNode = false;
    columnDefs: Array<GenericColDef>;
    gridName: string;
    colorizedQueryEntered: string = '';
    colorizedQueryExecuted: string = '';
    queryEntered: string = '';
    queryExecuted: string = '';

    rowClassRules = {
        'bold-row': (params: any): boolean => {
            switch (this.report.reportType) {
                case this.reportTypes.BILLING_REPORT: {
                    if (params && params.data && (params.data.documentClass === 'Total Excluded Types' || params.data.documentClass === 'Total Included Types')) {
                        return true;
                    }
                    break;
                }
                case this.reportTypes.TECHNICAL_REDUCTION:
                case this.reportTypes.TECHNICAL_REDUCTION_VERTICAL: {
                    if (params && params.data && (params.data.name === 'Total Documents')) {
                        return true;
                    }
                    break;
                }
                case this.reportTypes.IMPORT:
                case this.reportTypes.OCR_CANDIDATES:
                case this.reportTypes.EXPORT_COMPARE:
                case this.reportTypes.EXPORT_LOAD_FILE_REPORT:
                case this.reportTypes.EXPORT_PREPARE_REPORT:
                case this.reportTypes.EXPORT_PRODUCE_REPORT: {
                    if (params && params.data && (params.data.name.toLowerCase().indexOf('total') > -1) || params.data.isBold) {
                        return true;
                    }
                    break;
                }
            }
            return false;
        },
        'empty-row': (params: any): boolean => {
            return (params.data && params.data.emptyRow);
        },
        'shaded-row': (params: any): boolean => {
            switch (this.report.reportType) {
                case this.reportTypes.IMPORT:
                case this.reportTypes.OCR_CANDIDATES:
                case this.reportTypes.EXPORT_COMPARE:
                case this.reportTypes.EXPORT_LOAD_FILE_REPORT:
                case this.reportTypes.EXPORT_PREPARE_REPORT:
                case this.reportTypes.EXPORT_PRODUCE_REPORT: {
                    return !!(params?.data?.isBold &&
                        !params.data.name?.toLowerCase().includes('total') &&
                        params.data.name?.indexOf('resolvable)') < 0);
                }
            }
            return false;
        },
        'drillable-row': (params: any): boolean => {
            if (this.report.reportType === ReportType.EXPORT_PRODUCE_REPORT) {
                return !!(params?.data?.name?.indexOf('resolvable)') >= 0);
            }
            return false;
        }
    };

    reportTypes = ReportType;
    detailsType = DetailsType;
    reportDisplayType = ReportDisplayType;
    queryType = 'entered';
    searchTypes = ReportSearchType;
    contextMenuOptions: Array<ContextMenuOption>;
    rowSelection: GridRowSelectionType = GridRowSelectionType.multipleCheckbox;
    uniqueId: string;
    htmlLegendPlugin;

    canvas: any;
    ctx: any;
    chart: any;
    chartBySize = false;
    clickTimeout: any = 0; // this will hold setTimeout reference
    lastClick = 0; // last click timestamp
    doubleClickDuration = 300; // distance between clicks in ms - if it's less than that - it's a double click
    runDate: string;

    isChartBusy = false;
    isChatBusyMessage = 'Loading ...';

    chartColorScheme = ['#26a9e0', '#f6921e', '#d6de23', '#8bc53f', '#9e1f63', '#61b045', '#d4ae18', '#f7742c', '#9e0c40',
        '#136bc5', '#898cff', '#ff89b5', '#ffdc89', '#71e09', '#ed6d79', '#cff381', '#bb96ff', '#668de5', '#5a5255', '#FF6600'];

    startDate;
    endDate;
    granularity = 'YEAR';
    time = 'T00:00:00';
    defaultDatePlaceholders = ['Start YYYY-MM-DD ...', 'End YYYY-MM-DD ...'];
    dateError = false;
    hiddenActionText = false;
    hasDownloadPermission = true;
    setGranularity = true;
    showTooltips = true;  // Flag to control the display of tooltips
    prevBarElement = null;
    prevHoveredIndex = null;

    prevChartActiveElement: any;
    disableReportDownload: boolean;

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.checkTextAvailableWidth();
    }
    @HostListener('window:resizerResize', ['$event'])
    onResizerResize(event: any): void {
        this.checkTextAvailableWidth();
    }


    constructor(private xmlService: XMLService, private renderer: Renderer2, private restService: RestService,
        private commonService: CommonService, public changeDetector: ChangeDetectorRef,) {
        Chart.register(...registerables);
        Chart.register(ChartDataLabels);
        ChartJs.Chart.register(
            ChartJs.BarElement,
            ChartJs.BarController,
            ChartJs.CategoryScale,
            ChartJs.Decimation,
            ChartJs.Filler,
            ChartJs.Legend,
            ChartJs.Title,
            ChartJs.Tooltip,
            ...ChartJs.registerables
        );

        this.entityChangedEvent = this.commonService.getMessage(MessageType.enableReportDownload).subscribe((task) => {
            if (task.taskStatus === TaskStatus.SUCCESS) {
                this.disableReportDownload = false;
                this.addRemoveFromDownloadList(task, true);
            }
        });
    }

    ngOnInit(): void {
        this.disableReportDownload = !!(this.commonService.termSearchHitCountReportsDownloaded?.find(report => report.viewHandle === this.handle));
        if (this.report && this.report.reportType) {
            if (this.report.isFullWidth) {
                if (this.report.data && this.report.data.searchType === ReportSearchType.BULK) {
                    this.classes = 'no-overflow ';
                } else {
                    this.classes = '';
                }
                this.classes += 'full-width-report';
            } else {
                this.classes = 'half-width-report';
            }

            if (this?.report?.data?.searchType === 'By Profile') {
                this.report.data.searchType = 'By Synthetic Document';
            }
            if (this.report && this.report.data.runDate) {
                const convertedDates = [];
                const datetime = this.report.data.runDate.split('-');
                const datetime1 = datetime[2].split(' ');
                const datetime2 = datetime1[1].split(':');
                const convertedDate = new Date(datetime[0] + '-' + datetime[1] + '-' + datetime1[0] + 'T' + datetime2[0] + ':' + datetime2[1] + ':' + datetime2[2] + 'Z');
                convertedDates.push(convertedDate);
                this.runDate = convertedDates.length > 0 ? formatDate(new Date(Math.min.apply(null, convertedDates)), 'YYYY-MM-DD hh:mm:ss', null, false) : ' ';
            }
            this.queriesInitializationAndColorization();
            this.loadData();
            if (this.report.reportType === ReportType.WORKFLOW && this.workflow) {
                this.report.data.reportEntries.forEach(report => {
                    report.colorized = report.query.stepqueryDisplayed && !this.commonService.currentBrowserIsIE ?
                        (window as any).hljs?.highlightAuto(report.query.stepqueryDisplayed).value : report.query.stepqueryDisplayed;
                });
            }
            if (this.report.reportType === ReportType.SEARCH && this.report.node?.data?.task === SearchType.BULK) {
                this.rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
            }
            this.hasDownloadPermission = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);
            this.uniqueId = performance.now() + '_' + this.report.index;

            //generate chart legend
            this.htmlLegendPlugin = {
                id: this.uniqueId + '-legend',
                afterInit: (chart, args, options) => {
                    const ul = this.getOrCreateLegendList(chart, options.containerID);

                    if (ul) {
                        //remove old legend items
                        while (ul.firstChild) {
                            ul.firstChild.remove();
                        }
                        const items = chart.options?.plugins?.legend?.labels?.generateLabels(chart);
                        items.forEach(item => {
                            const li = document.createElement('li');
                            li.className = 'legend-list-item';

                            //color box
                            const colorBoxSpan = document.createElement('span');
                            colorBoxSpan.className = 'box-span';
                            //colorBoxSpan.style.backgroundColor = item.fillStyle;
                            colorBoxSpan.style.backgroundColor = this.chartColorScheme[item.index];;

                            //text
                            const textContainer = document.createElement('p');
                            textContainer.className = 'text-container';
                            const text = document.createTextNode(item.text);

                            //title
                            li.title = 'Double-click to drill through.';
                            // click event
                            if (!li.getAttribute('data-listener-added')) {
                                li.addEventListener('click', this.handleClick, false);
                                li.addEventListener('mouseenter', () => this.showTooltip(chart, item.index));
                                li.addEventListener('mouseleave', () => this.hideTooltip(chart, chart.ctx));
                            }

                            textContainer.appendChild(text);
                            li.appendChild(colorBoxSpan);
                            li.appendChild(textContainer);
                            ul.appendChild(li);
                            if (!ul.getAttribute('data-listener-added')) {
                                ul.addEventListener('mouseleave', () => this.hideTooltip(chart, null));
                            }
                        });
                    }
                }
            };
        }
    }

    queriesInitializationAndColorization(): void {
        if (this.report.data && (this.report.data.queryEntered || this.report.data.queryExecuted)) {
            this.queryEntered = this.report.data?.queryEntered?.value ? this.report.data.queryEntered.value : this.report.data?.queryEntered;
            this.queryExecuted = this.report.data?.queryExecuted?.value ? this.report.data.queryExecuted.value : this.report.data?.queryExecuted;

            const queryEnteredTrimmed = this.returnQueryStringToDisplay(this.report.data.queryEntered);
            const queryExecutedTrimmed = this.returnQueryStringToDisplay(this.report.data.queryExecuted);

            this.colorizedQueryEntered = this.report.data.queryEntered && !this.commonService.currentBrowserIsIE ?
                (window as any).hljs?.highlightAuto(queryEnteredTrimmed).value : queryEnteredTrimmed;
            this.colorizedQueryExecuted = this.report.data.queryExecuted && !this.commonService.currentBrowserIsIE ?
                (window as any).hljs?.highlightAuto(queryExecutedTrimmed).value : queryExecutedTrimmed;
        }
    }

    returnQueryStringToDisplay(value: string): string {
        if (value?.length > MAX_LIMIT_OF_RENDERED_QUERY) {
            return value.slice(0, MAX_LIMIT_OF_RENDERED_QUERY) + ' ...';
        }
        return value;
    }

    showTooltip(chart, index): void {
        if (chart?.getDataVisibility(index) === true) {
            const { type } = chart.config;
            if (type === 'pie') {
                this.showTooltips = false;
                chart.options.plugins.datalabels.display = false;
                chart?.setActiveElements([
                    {
                        datasetIndex: 0,
                        index: index
                    }
                ]);
            } else {
                this.showTooltips = true;
                chart.setActiveElements([]);
            }

            const tooltip = chart.tooltip;
            chart?.tooltip?.setActiveElements([{ datasetIndex: 0, index }]);
            chart?.update();
            tooltip?.update(true);
            tooltip?.draw();
        }
    }

    hideTooltip(chart, ctx): void {
        if (chart) {
            const tooltip = chart?.tooltip;
            tooltip?.setActiveElements([]);
            chart.options.plugins.datalabels.display = true;
            const segments = chart.getDatasetMeta(0).data;
            for (let j = 0; j < segments.length; j++) {
                chart.data.datasets[0].backgroundColor[segments[j].$context.index] = this.chartColorScheme[segments[j].$context.index];
            }
            chart.setActiveElements([]);
            this.chart?.update();
        }
    }

    triggerAfterEvent() {
        if (this.chart) {
            const event = {
                type: 'afterEvent',
                chart: this.chart,
                args: {
                    type: 'manual'
                }
            };

            // Iterate through plugins and manually call afterEvent
            this.chart.config._config.plugins.forEach(plugin => {
                if (plugin.afterEvent) {
                    plugin.afterEvent(this.chart, event);
                }
            });
        }
    }

    ngAfterContentChecked(): void {
        this.changeDetector.detectChanges();
    }

    loadData(): void {
        if (this.report.reportType === ReportType.TERM_SEARCH_HIT_COUNT || this.report.reportType === ReportType.TERM_SEARCH_HIT_COUNT_VERTICAL) {
            let clauseColumnName = null;
            this.restService.post(Endpoint.projectManager_getSearchSettings, { handle: this.commonService.currentProject.handle }).then(response => {
                if (response && response.searchSettings && response.searchSettings.searchTermMetrics && response.searchSettings.searchTermMetrics.length > 0) {
                    this.columnDefs = [];
                    response.searchSettings.searchTermMetrics.forEach(column => {
                        if (column.enabled) {
                            const columnDef = {
                                headerName: column.effectiveLabel.replace(/ Filesize/g, ' File Size'),
                                automationId: '',
                                field: column.effectiveLabel.replace(/ /g, ''),
                                minWidth: 120,
                                headerAlignDirection: AlignDirection.right,
                                cellAlignDirection: AlignDirection.right,
                                headerClass: 'report-header'
                            } as GenericColDef;
                            switch (column.defaultLabel) {
                                case 'ID': {
                                    columnDef.minWidth = 50;
                                    columnDef.width = 50;
                                    columnDef.sortBy = 'id';
                                    break;
                                }
                                case 'Clause': {
                                    columnDef.minWidth = 180;
                                    columnDef.headerAlignDirection = AlignDirection.left;
                                    columnDef.cellAlignDirection = AlignDirection.left;
                                    columnDef.cellClass = 'bold-font';
                                    clauseColumnName = columnDef.field;
                                    this.report.clauseColumnName = clauseColumnName;
                                    break;
                                }
                                case 'Doc Count':
                                case 'Unique':
                                case 'Clause Ratio': {
                                    columnDef.minWidth = 70;
                                    break;
                                }
                                case 'Duplicates':
                                case 'Family Expansions':
                                case 'Family Doc Count': {
                                    columnDef.minWidth = 90;
                                    break;
                                }
                                case 'Total DeDuped Filesize w/Family':
                                case 'Unique DeDuped Families Filesize': {
                                    columnDef.minWidth = 130;
                                    break;
                                }
                            }
                            this.columnDefs.push(columnDef);
                        }
                    });
                }
                if (clauseColumnName) {
                    if (this.report.data.formatedData && this.report.data.formatedData.length > 0) {
                        this.report.data.formatedData.forEach(data => {
                            const clauseValue = this.xmlService.xmlToQueryConverter(data[clauseColumnName].substring(1, data[clauseColumnName].length - 1), true) as any;
                            let clauseString = clauseValue.query && isString(clauseValue.query) ? clauseValue.query.replace(/</g, '&lt;').replace(/>/g, '&gt;') : clauseValue.query;
                            if (clauseValue.field) {
                                const clauseStartField = clauseValue.field + '::';
                                if (clauseString.startsWith(clauseStartField)) {
                                    const clauseStringWithoutField = clauseString.substring(clauseStartField.length, clauseString.length - 1).replace(/^\(|\)$/g, '');
                                    clauseString = clauseStartField + clauseStringWithoutField;
                                } else {
                                    clauseString = `${clauseValue.field}::(${clauseString})`;
                                }
                            }
                            data[clauseColumnName] = clauseString;
                        });
                    }
                    if (this.report.data.pinnedRow) {
                        this.report.data.pinnedRow[clauseColumnName] = this.xmlService.xmlToQueryConverter(this.report.data.pinnedRow[clauseColumnName]
                            .substring(1, this.report.data.pinnedRow[clauseColumnName].length - 1));
                    }
                }
            });
        } else {
            this.columnDefs = ReportDefinitions.get(ReportType[ReportType[this.report.reportType]]);
        }
        this.chartBySize = this.report.chartBySize;
    }

    responseCallback = (response: any): void => {
        if (response) {
            return;
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode): void => {
        if (this.report.reportType === ReportType.EXPORT_PRODUCE_REPORT && rowNode?.data?.name.indexOf('resolvable)') >= 0 && rowNode?.data?.count !== 0 && rowNode?.data?.actualCount !== 0) {
            this.exportErrorsMetaDataBulkSearch(rowNode.data);
        } else if (this.report.data && this.report.data.searchType === ReportSearchType.BULK) {
            // not obvious naming but search result handle comes as the value of 'unformattedSize' parameter
            const formattedBulkSearchNode = {
                handle: rowNode?.data?.data?.unformattedSize,
                documentCount: rowNode?.data?.data?.documentCount,
                entityType: UpdateStatusType.RESULT_VIEW,
                description: rowNode.data.name + ' in ' + this.report.data.targetName,
                task: SearchType.SEARCH_FROM_BULK_SEARCH,
                taskType: TaskType.OTHER,
                viewType: this.report.node?.data?.viewType,
                attributes: [{ name: "viewtype", value: this.report.node?.data?.viewType }]
            };
            this.commonService.sendMessage(MessageType.navigationRequest, formattedBulkSearchNode);
        } else if (rowNode && rowNode.data) {
            const value = this.report.reportType === ReportType.BILLING_REPORT ? rowNode.data.documentClass : (rowNode.data.name ? rowNode.data.name.trim() : rowNode.data.name);
            if (((this.report.reportType === ReportType.TECHNICAL_REDUCTION || this.report.reportType === ReportType.TECHNICAL_REDUCTION_VERTICAL) &&
                (value === 'Total' || value === 'Total Documents')) || (this.report.reportType === ReportType.BILLING_REPORT)) {
                return;
            } else {
                if ((rowNode.data && rowNode.data.size && rowNode.data.count) ||
                    (this.report.reportType === ReportType.OCR_SOURCE && rowNode.data && rowNode.data.nonFormattedNameNotProcessed && parseInt(rowNode.data.notYet, 10) > 0)) {
                    this.drillThrough(null, rowNode.data);
                }
            }
        }
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
        if (rowNode && rowNode.data && rowNode.data.name && action) {
            switch (action) {
                case CellActionType.linkLabelClick: {
                    const request = {
                        entryName: rowNode.data.name,
                        hideSearchWb: true,
                        reportType: this.report.reportType,
                        searchDialect: this.commonService.searchDialect,
                        viewHandle: this.handle
                    };
                    this.restService.post(Endpoint.viewManager_drillDownOnReport, request).then(response => {
                        this.openDetailsModal(DetailsType.EXCEPTION_DETAILS, response.viewHandle);
                    });
                    break;
                }
            }
        }
    }

    checkScroll(): boolean {
        if (document.getElementById('stepsPane')) {
            return document.getElementById('stepsPane').clientHeight < document.getElementById('stepsPane').scrollHeight;
        }
    }

    ngAfterViewInit(): void {
        if (this.rightSection && this.rightSection.nativeElement && this.leftSection && this.leftSection.nativeElement) {
            let height = 20;
            for (const item of this.leftSection.nativeElement.children) {
                height += item.clientHeight;
            }
            if (height < this.leftSection.nativeElement.clientHeight) {
                this.renderer.setStyle(this.leftSection.nativeElement, 'height', height + 'px');
            }
            if (this.rightSection.nativeElement.clientHeight > 250 && this.report.data.searchType === ReportSearchType.DRILL_THROUGH) {
                this.renderer.setStyle(this.leftSection.nativeElement, 'height', '200px');
            }
            this.renderer.setStyle(this.rightSection.nativeElement, 'height', this.leftSection.nativeElement.clientHeight + 'px');
        }
        if (this.report) {
            this.drawChart();
        }
    }

    drawChart(): void {
        if (this.report.reportDisplayType === this.reportDisplayType.PIE_CHART && this.report.data.reportEntryList && this.report.data.reportEntryList.length > 0) {
            this.pieChart();
        } else if (this.report.reportDisplayType === this.reportDisplayType.BAR_CHART && this.report.data.reportEntryList && this.report.data.reportEntryList.length > 0) {
            this.barChart();
            if (this.report.isZeroDayDetailsButtonPresent && this.setGranularity) {
                this.setInitialGranularity();
                this.setGranularity = false;
            }
        }
    }

    setInitialGranularity(): void {
        if (this.report && this.report.data && this.report.data.reportEntryList && this.report.data.reportEntryList.length > 0) {
            this.report.data.reportEntryList = _.sortBy(this.report.data.reportEntryList, 'name');
            const firstReportData = this.report.data.reportEntryList[0];
            const lastReportData = this.report.data.reportEntryList[this.report.data.reportEntryList.length - 1];
            if (firstReportData) {
                const startYear = parseInt(firstReportData.name, 10);
                this.startDate = isNaN(startYear) ? null : new Date(startYear, 0, 1);
            }
            if (lastReportData) {
                const endYear = parseInt(lastReportData.name, 10);
                this.endDate = isNaN(endYear) ? null : new Date(endYear, 11, 31);
            }
            this.changeDetector.detectChanges();
        }
    }

    isQuerySectionVisible(): boolean {
        if (this.report.data.searchType === ReportSearchType.FREEFORM || this.report.data.searchType === ReportSearchType.ADVANCED ||
            this.report.data.searchType === ReportSearchType.COMBINED || this.report.data.searchType === ReportSearchType.DRILL_THROUGH) {
            return true;
        }
        return false;
    }

    setQueryType(value: any): void {
        this.queryType = value;
    }

    public downloadFile(report: any, extension: any): void {
        if (report === ReportType.SEARCH && this.commonService.selectedNavigationNode?.data?.task !== SearchType.BULK) {
            this.generateSearchTermReport(report);
        } else {
            this.downloadReport.emit({ report: report, extension: extension });
        }
    }

    generateSearchTermReport(report: any): void {
        this.disableReportDownload = true;
        const params = {
            viewHandle: this.handle,
            taskDescription: "Creating TERM_SEARCH_HIT_COUNT for downloading for " + this.commonService.selectedNavigationNode?.name,
            projectHandle: this.commonService.currentProject.handle,
            reportType: ReportType.TERM_SEARCH_HIT_COUNT
        };

        this.restService.post(Endpoint.projectManager_generateDownloadFromWorkbasketReport, params).then(response => {
            if (response?.taskStatus !== TaskStatus.SUCCESS) {
                this.addRemoveFromDownloadList(response);
            }
            if (response?.taskStatus === TaskStatus.SUCCESS) {
                this.disableReportDownload = false;
                this.addRemoveFromDownloadList(response, true);
            }
        }, error => {
            this.disableReportDownload = false;
        });
        this.commonService.openWarningModal('The report is being generated. Please wait for this operation to finish.', 'Warning');
    }

    addRemoveFromDownloadList(response: any, isRemove?: boolean): void {
        const isReportDownloaded = this.commonService.termSearchHitCountReportsDownloaded?.find(report => report?.handle === response.handle);
        if (!isReportDownloaded) {
            this.commonService.termSearchHitCountReportsDownloaded.push({ viewHandle: this.handle, handle: response.handle });
        }
        if (isRemove) {
            this.commonService.termSearchHitCountReportsDownloaded = this.commonService.termSearchHitCountReportsDownloaded.filter(report => report?.handle !== response.handle);
        }
    }


    openDetailsModal(detailsType: any, viewHandle = null): void {
        const mapedReportDetails = ReportDetailsMap.get(DetailsType[DetailsType[detailsType]])(this.report);
        const width = this.report.reportType === ReportType.REVIEW_FOLDER ?
            ' horizontal-percent-80' :
            ' horizontal-px-' + (mapedReportDetails.modalWidth ? mapedReportDetails.modalWidth : '500');
        this.commonService.openModal(ReportsDetailsModalComponent, {
            modalData: <ReportsDetailsModalData>{
                detailType: detailsType,
                handle: viewHandle ? viewHandle : this.report.node && this.report.node.data instanceof Comparison ? this.handle :
                    this.report.node.name === 'Dashboard' ? this.commonService.projectData.handle :
                        this.report.node.data && this.report.node.data.view ? this.report.node.data.view.handle : this.report.node.data.handle,
                title: mapedReportDetails.title,
                helpIndex: mapedReportDetails.helpIndex,
                reportDetails: mapedReportDetails,
                startDate: this.startDate,
                endDate: this.endDate
            },
            class: 'vertical-px-' + (mapedReportDetails.modalHeight ? mapedReportDetails.modalHeight : '500') +
                width
        });
    }

    showNoDataOrDisabledPanel(report: any): boolean {
        // this condition is to make sure EXPORT_COMPARE report's respons is available from the backend and it's
        // reportEntryList is used as the data for EXPORT_COMPARE_STATISTICS report
        const exportCompareReport = _.findWhere(this.allReports, { reportType: ReportType.EXPORT_COMPARE });
        if (exportCompareReport && exportCompareReport.data && exportCompareReport.data.reportEntryList
            && exportCompareReport.data.reportEntryList.length > 0 && report.reportType === ReportType.EXPORT_COMPARE_STATISTICS) {
            report.data.reportType = ReportType.EXPORT_COMPARE_STATISTICS;
            report.data.formatedData = exportCompareReport.data.reportEntryList;
        }
        if (report) {
            const reportType = report.data ? report.data.reportType : null;
            if (!reportType) {
                return report.reportType !== ReportType.SEARCH;
            } else {
                if (reportType !== ReportType.SEARCH && report.data && report.data.formatedData && report.data.formatedData.length === 0) {
                    return true;
                } else if (reportType === ReportType.TECHNICAL_REDUCTION && report.isDisabled) {
                    return true;
                } else if (reportType === ReportType.TECHNICAL_REDUCTION_VERTICAL && report.isDisabled) {
                    return true;
                }
            }
        }
        return false;
    }

    barChart(): void {
        Chart.defaults.plugins.legend.display = false;

        const barColor = function () {
            return '#74c0d3';
        };

        const config = {
            type: 'bar',
            data: {
                labels: this.chartDataSets(this.reportDisplayType.BAR_CHART).labelsTrimmed,
                datasets: [{
                    data: this.chartDataSets(this.reportDisplayType.BAR_CHART).datasets,
                    fill: false,
                    borderDash: [5, 5],
                    borderColor: barColor(),
                    backgroundColor: (slice) => {
                        const chart = this.chart;
                        if (chart) {
                            if (chart?.tooltip?._active?.length) {
                                const index = chart.tooltip._active[0].index;;
                                if (index === slice.index) {
                                    return '#72B0C0';
                                } else {
                                    return '#eaeaea';
                                }
                            } else {
                                return barColor();
                            }
                        } else {
                            return barColor();
                        }
                    },
                    pointBorderColor: barColor(),
                    pointBackgroundColor: barColor(),
                    pointBorderWidth: 1,
                }]
            },
            plugins: [{
                afterEvent: (chart, e) => {
                    const elements = this.chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true);
                    const segments = this.chart.getDatasetMeta(0).data;

                    if (e?.event?.chart?.tooltip?._active?.length) {
                        this.chart.options.plugins.datalabels.display = false;
                        const index = e?.event?.chart?.tooltip?._active[0].index;

                        // Only proceed if the hovered element has changed
                        if (index !== this.prevBarElement) {
                            this.prevBarElement = index;
                            chart.update();
                        }
                    } else {
                        // Only reset if there was a previously hovered element
                        if (this.prevBarElement !== null) {
                            this.prevBarElement = null;
                            this.chart.options.plugins.datalabels.display = true;
                            chart.update();
                        }
                    }
                }
            }],
            options: {
                onResize: function (chart) {
                    chart.update(0);
                },
                legend: {
                    display: false
                },
                plugins: {
                    tooltip: {
                        enabled: false,
                        mode: 'index',
                        intersect: false,
                        axis: 'x',
                        yAlign: 'bottom',
                        xAlign: 'center',
                        caret: false,
                        caretSize: 0,
                        backgroundColor: '#858585',
                        bodySpacing: 1,
                        boxPadding: 4,
                        cornerRadius: 5,
                        borderWidth: 1,
                        bodyFont: {
                            family: 'Roboto',
                            size: 12,
                        },
                        padding: {
                            x: 5,
                            y: 3
                        },
                        borderColor: '#858585',
                        displayColors: false,
                        callbacks: {
                            label: (tooltipItems: any) => {
                                const chartContainer = document.getElementById('chart-container' + '_' + this.report.index);
                                const tooltipDrillHistogram = document.getElementById('tooltip-drill-histogram-text' + '_' + this.report.index);

                                if (chartContainer) {
                                    chartContainer.onmousemove = (e) => {
                                        if (tooltipDrillHistogram && tooltipItems && this.chart && this.chart.tooltip._active && this.chart.tooltip._active.length > 0 && this.report.isZeroDayDetailsButtonPresent) {
                                            const mouseTextPosition = e.clientX,
                                                mouseYPosition = e.clientY,
                                                mouseCursorPosition = e.offsetX;

                                            tooltipDrillHistogram.style.top = (mouseYPosition + 20) + 'px';

                                            tooltipDrillHistogram.style.top = (mouseYPosition + 18) + 'px';
                                            if (mouseTextPosition - 40 > chartContainer.offsetWidth) {
                                                tooltipDrillHistogram.style.left = mouseCursorPosition + 53 + 'px';
                                            } else {
                                                tooltipDrillHistogram.style.left = (mouseTextPosition + 17) + 'px';
                                            }

                                            tooltipDrillHistogram.style.display = 'block';
                                            tooltipDrillHistogram.style.position = 'fixed';
                                            tooltipDrillHistogram.style.overflow = 'hidden';
                                        }
                                    };

                                    chartContainer.onmouseout = (e: MouseEvent): void => {
                                        if (tooltipDrillHistogram) {
                                            tooltipDrillHistogram.style.display = 'none';
                                        }
                                    };

                                    chartContainer.onmouseleave = (e) => {
                                        if (tooltipDrillHistogram) {
                                            tooltipDrillHistogram.style.display = 'none';
                                        }
                                    };
                                }

                                if (this.chartBySize) {
                                    const sizeConverted = this.commonService.formatSizeUnits(tooltipItems.raw);
                                    if (this.chart && this.chart.options && this.chart.config._config.options.showAllTooltips) {
                                        return sizeConverted;
                                    } else {
                                        if (this.chart && this.chart.tooltip._active && this.chart.tooltip._active.length > 0) {
                                            return (tooltipItems.label) + ': ' + sizeConverted;
                                        } else {
                                            return sizeConverted;
                                        }
                                    }
                                }
                                if (this.chart && this.chart.tooltip._active && this.chart.tooltip._active.length > 0) {
                                    return tooltipItems.label + ': ' + (tooltipItems.raw);
                                }
                                return (tooltipItems.raw);
                            },
                            title: function () {
                                return '';
                            },
                        },
                        external: function (context) {
                            // Tooltip Element
                            let tooltipEl: any = document.getElementById('chartjs-tooltip-bar');

                            // Create element on first render
                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div');
                                tooltipEl.id = 'chartjs-tooltip-bar';
                                tooltipEl.innerHTML = '<table></table>';
                                document.body.appendChild(tooltipEl);
                            }

                            // Hide if no tooltip
                            const tooltipModel = context.tooltip;
                            if (tooltipModel.opacity === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            let bodyLines = null;
                            // Set Text
                            if (tooltipModel.body) {
                                bodyLines = tooltipModel.body.map(b => b.lines);

                                const innerHtml = '<div style="background: #858585;border-color: #858585;border-width: 2px;height: 20px;border-radius: 7.5px; color: white; padding: 1px 4px 2px 4px;">'
                                    + bodyLines.join('<br>') + '</div>';

                                tooltipEl.innerHTML = innerHtml;
                            }

                            // Display, position, and set styles for font
                            const position = context.chart.canvas.getBoundingClientRect();

                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.position = 'absolute';
                            tooltipEl.style.left = position.left + window.pageXOffset + (tooltipModel.caretX - ((bodyLines[0][0].length * 2) + 12)) + 'px';
                            tooltipEl.style.top = position.top + window.pageYOffset + (tooltipModel.caretY - 27) + 'px';
                        }
                    },
                    datalabels: {
                        display: true,
                        align: 'end',
                        anchor: 'end',
                        color: function (context) {
                            return 'white';
                        },
                        formatter: (value, context) => {
                            if (this.chartBySize) {
                                const sizeConverted = this.commonService.formatSizeUnits(value);
                                if (this.chart && this.chart.options && this.chart.config._config.options.showAllTooltips) {
                                    return sizeConverted;
                                } else {
                                    if (this.chart && this.chart?.tooltip?._active && this.chart?.tooltip?._active?.length > 0) {
                                        return (this.chart.getDatasetMeta(0).data[context.dataIndex].label) + ': ' + sizeConverted;
                                    } else {
                                        return sizeConverted;
                                    }
                                }
                            }
                            if (this.chart && this.chart?.tooltip?._active && this.chart?.tooltip?._active?.length > 0) {
                                return this.chart?.tooltip?._active[0].element.label + ': ' + (value);
                            }
                            return (value);
                        },
                        backgroundColor: function (context) {
                            return '#858585';
                        },
                        borderColor: function (context) {
                            return '#858585';
                        },
                        font: function (context) {
                            var w = context.chart.width;
                            return {
                                family: 'Roboto',
                                size: 12,
                                weight: '200',
                            };
                        },
                        padding: {
                            top: '4',
                            left: '5',
                            right: '5',
                            bottom: '2',
                        },
                        borderRadius: 7.5,
                        borderWidth: 0,
                        offset: 6,
                        listeners: {
                            enter: function (context) {
                                context.hovered = true;
                                return true;
                            },
                            leave: function (context) {
                                context.hovered = false;
                                return true;
                            }
                        }
                    },
                },
                layout: {
                    padding: {
                        left: 10,
                        right: 10,
                        top: 25,
                        bottom: 10
                    }
                },
                scales: {
                    x: {
                        display: true,
                        scaleLabel: {
                            show: true,
                        },
                        gridLines: {
                            offsetGridLines: true
                        }
                    },
                    y: {
                        beginAtZero: true,
                        display: true,
                        scaleLabel: {
                            show: true,
                        },
                        ticks: {
                            suggestedMin: 0,
                            suggestedMax: this.chartDataSets(this.reportDisplayType.BAR_CHART).suggestedMax,
                            beginAtZero: true,
                            callback: (value, index, values) => {
                                if (this.chartBySize) {
                                    return this.commonService.formatSizeUnits(value);
                                } else {
                                    return value;
                                }
                            }
                        }
                    }
                },
                hover: {
                    mode: 'x-axis',
                    intersect: true,
                    animationDuration: 0 // Disable hover animation
                },
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                animation: {
                    duration: 0 // Disable animation for hover
                },
                responsive: true,
                maintainAspectRatio: false,
                showTooltips: true,
                showAllTooltips: true,
                responsiveAnimationDuration: 0,
                onClick: this.handleClick,
            }
        };

        this.renderChart(config, this.reportDisplayType.BAR_CHART);
    }

    pieChart(): void {
        const chartX: any = ChartJs;

        const config = {
            type: 'pie',
            data: {
                labels: this.chartDataSets(this.reportDisplayType.PIE_CHART).labels,
                datasets: [{
                    data: this.chartDataSets(this.reportDisplayType.PIE_CHART).datasets,
                    backgroundColor: this.chartColorScheme.slice(),
                    borderWidth: 0,
                    hoverBackgroundColor: (slice) => {
                        const segments = this.chart?.getDatasetMeta(0)?.data;

                        if (this.chart?._active?.length && this.chart?._active[0]) {
                            for (let j = 0; j < segments.length; j++) {
                                if (segments[j].$context.element.index !== slice.index) {
                                    segments[j].$context.element.options.backgroundColor = 'rgba(220,220,220,0.6)';
                                    this.chart.data.datasets[0].backgroundColor[segments[j].$context.index] = 'rgba(220,220,220,0.6)';
                                }
                            }
                        } else {
                            for (let j = 0; j < segments?.length; j++) {
                                this.chart.data.datasets[0].backgroundColor[segments[j].$context.index] = this.chartColorScheme[segments[j].$context.index];
                            }
                        }

                        slice.element.options.backgroundColor = this.chartColorScheme[slice.index];
                    },
                    hoverBorderWidth: 0,
                    borderColor: ['rgba(220,220,220,0.6)'],
                }]
            },
            plugins: [
                {
                    afterDraw: (chart, easing) => {
                        if (!this.showTooltips) return;  // Only draw tooltips if allowed
                        const { ctx } = chart;
                        if (!chart._active || !chart._active.length) {
                            this.hideTooltip(chart, null);
                        }
                        ctx.save();
                        // turn off normal tooltips
                        chart.config._config.options.plugins.tooltip = false;
                    },
                }, this.htmlLegendPlugin],
            options: {
                interaction: {
                    animation: false // Disable hover animation
                },
                showAllTooltips: true,
                zoomOutPercentage: 1, // makes chart 40% smaller (50% by default, if the property is undefined)
                responsive: true,
                maintainAspectRatio: false,
                responsiveAnimationDuration: 0,
                animation: 0,
                resizeDelay: 0,
                onResize: (chart, size) => {
                    chart.update(0);
                    if (size?.width < 12) {
                        const lastIndex = chart?.getDatasetMeta(0)?.data?.at(-1) ? chart.getDatasetMeta(0).data.length - 1 : null;
                        this.showTooltip(chart, lastIndex)
                        this.hideTooltip(chart, null);
                    }
                },
                onHover: (event, chartElement) => {
                    const chart = event.chart;
                    if (chartElement.length) {
                        const sliceIndex = chartElement[0].index;
                        this.showTooltip(chart, sliceIndex);
                    } else {
                        this.hideTooltip(chart, null);
                    }
                },
                hoverOffset: 0,
                onClick: this.handleClick,
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 10,
                        bottom: 10
                    }
                },
                plugins: {
                    layout: {
                        padding: {
                            top: 20, // Add some padding to avoid label clipping at the top
                        }
                    },
                    datalabels: {
                        labels: {
                            value: {
                                anchor: 'end',  // Anchor the label at the end of the data element
                                align: 'end', // Align the label to the start (left) of the anchor point
                                offset: -95,     // Offset the label by 10 pixels from the anchor point
                                backgroundColor: (ctx) => {
                                    const datasets = ctx.chart.config.data.datasets[0];
                                    const total = datasets.data.reduce(function (previousValue, currentValue, currentIndex, array) {
                                        return Number(previousValue) + Number(currentValue);
                                    });
                                    const currentValueData = datasets.data[ctx.dataIndex];
                                    const percentage = Math.floor(((currentValueData / total) * 100) + 0.5);
                                    if (percentage > 7 && ctx?.chart?._active.length) {
                                        return ctx.chart._active[0].pieTooltipFillColor;
                                    } else {
                                        return this.shadeColor(this.chartColorScheme[ctx.dataIndex], -30);
                                    }
                                },
                                borderColor: (ctx) => {
                                    const bgColor = this.shadeColor(ctx.dataset.backgroundColor[ctx.dataIndex], -30);
                                    return bgColor;
                                },
                                borderRadius: 5,
                                borderWidth: 0,
                                rotation: 0,
                                padding: {
                                    top: '4',
                                    left: '5',
                                    right: '5',
                                    bottom: '2',
                                },
                                color: function (ctx) {
                                    return 'white';
                                },
                                formatter: (value, ctx) => {
                                    const datasets = ctx.chart.config.data.datasets[0];
                                    const total = datasets.data.reduce(function (previousValue, currentValue, currentIndex, array) {
                                        return Number(previousValue) + Number(currentValue);
                                    });
                                    const currentValueData = datasets.data[ctx.dataIndex];
                                    const percentage = Math.floor(((currentValueData / total) * 100) + 0.5);
                                    if (percentage > 7) {
                                        if (this.chartBySize) {
                                            const sizeConverted = this.commonService.formatSizeUnits(ctx.dataset.data[ctx.dataIndex]);
                                            const text = ctx.chart.data.labels[ctx.dataIndex] + ' : ' + sizeConverted;
                                            return text;
                                        }
                                        return ctx.chart.data.labels[ctx.dataIndex] + ' : ' + Number(ctx.dataset.data[ctx.dataIndex]).toLocaleString();
                                    } else {
                                        return null;
                                    }
                                },
                                clip: true,
                            }
                        }
                    },
                    legend: {
                        display: false
                    },
                    htmlLegend: {
                        containerID: this.uniqueId + '-legend'
                    },
                    tooltip: {
                        enabled: false,
                        displayColors: false,
                        showAllTooltips: true,
                        titleFontSize: 12,
                        bodyFontSize: 12,
                        bodyFontFamily: 'Roboto',
                        bodySpacing: 1,
                        intersect: true,
                        mode: 'nearest',
                        xPadding: 4,
                        yPadding: 3,
                        xAlign: 'center',
                        yAlign: 'top',
                        caretSize: 0,
                        callbacks: {
                            label: (tooltipItems) => {
                                if (this.chartBySize) {
                                    const sizeConverted = this.commonService.formatSizeUnits(parseInt(tooltipItems.formattedValue.replace(/,/g, ''), 10));
                                    tooltipItems.element.options.backgroundColor = tooltipItems.element.pieTooltipFillColor;
                                    return (tooltipItems.label) + ': ' + sizeConverted;
                                }
                                return (tooltipItems.label) + ': ' + (tooltipItems.formattedValue);
                            },
                            labelColor: function (tooltipItem, chart) {
                                if (tooltipItem && tooltipItem.datasetIndex) {
                                    chart.tooltip.draw();
                                    return {
                                        backgroundColor: tooltipItem.element.pieTooltipFillColor
                                    };
                                } else {
                                    return {
                                        backgroundColor: tooltipItem.element.pieTooltipFillColor
                                    };
                                }
                            },
                            title: (chart) => {
                                return '';
                            }
                        },
                        external: function (context) {
                            // Tooltip Element
                            let tooltipEl: any = document.getElementById('chartjs-tooltip');

                            // Create element on first render
                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div');
                                tooltipEl.id = 'chartjs-tooltip';
                                tooltipEl.innerHTML = '<table></table>';
                                document.body.appendChild(tooltipEl);
                            }

                            // Hide if no tooltip
                            const tooltipModel = context.tooltip;
                            if (tooltipModel.opacity === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }

                            // Set caret Position
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltipModel.yAlign) {
                                tooltipEl.classList.add(tooltipModel.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }

                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }

                            // Set Text
                            if (tooltipModel.body) {
                                const titleLines = tooltipModel.title || [];
                                const bodyLines = tooltipModel.body.map(getBody);

                                let innerHtml = '<thead>';

                                titleLines.forEach(function (title) {
                                    innerHtml += '<tr><th>' + title + '</th></tr>';
                                });
                                innerHtml += '</thead><tbody>';

                                bodyLines.forEach(function (body, i) {
                                    const colors = tooltipModel.labelColors[i];
                                    let style = 'background:' + tooltipModel.dataPoints[0].element.pieTooltipFillColor;
                                    style += '; border-color:' + tooltipModel.dataPoints[0].element.pieTooltipFillColor;
                                    style += '; border-width: 2px';
                                    style += '; height: 10px';
                                    style += '; border-radius: 5px;';
                                    style += '; color: white;';
                                    style += '; padding: 2px 4px 2px 4px;';
                                    const span = '<span style="' + style + '">' + body + '</span>';
                                    innerHtml += '<tr><td>' + span + '</td></tr>';
                                });
                                innerHtml += '</tbody>';

                                let tableRoot = tooltipEl.querySelector('table');
                                tableRoot.innerHTML = innerHtml;
                            }

                            const position = context.chart.canvas.getBoundingClientRect();
                            //const bodyFont = Chart.helpers.toFont(tooltipModel.options.bodyFont);

                            // Display, position, and set styles for font
                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.position = 'absolute';
                            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                            //tooltipEl.style.font = bodyFont.string;
                            tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
                            tooltipEl.style.pointerEvents = 'none';
                        }
                    }
                },
            },
        };

        this.renderChart(config, this.reportDisplayType.PIE_CHART);
    }

    getOrCreateLegendList = (chart, id) => {
        const legendContainer = this.getLegend();
        if (legendContainer) {
            let listContainer = legendContainer.querySelector('ul');
            if (!listContainer) {
                listContainer = document.createElement('ul');
                listContainer.className = '2-legend';
                legendContainer.appendChild(listContainer);
            }
            return listContainer;
        }
    }

    exportErrorsMetaDataBulkSearch(data: any): void {
        this.reportGrid.isBusy = true;
        this.reportGrid.isBusyMessage = 'Drilling through ...';;
        const isNative = data.name.indexOf('Native') > -1;
        const requestData = {
            attributes: [{ 'name': 'exportErrorsMetaDataBulkSearch', 'value': 'exportErrorsMetaDataBulkSearch' }],
            includeFamily: false,
            metaDataFieldName: 'docnum',
            targetViewHandle: this.commonService.projectData.handle,
            taskDescription: `Drill through ${isNative ? 'Native' : 'Text'}_Error for ${this.commonService?.selectedNavigationNode?.data?.name}`,
            dataAreaHandle: '',
            values: [],
            wbHandle: this.report?.data?.wbHandle ? this.report.data.wbHandle : null,
            exportErrorName: data.name
        };
        this.restService.post(Endpoint.viewManager_exportErrorsMetaDataBulkSearch, requestData).then(() => {
            this.reportGrid.isBusy = false;
        }, error => {
            this.reportGrid.isBusy = false;
        });
    }

    drillThrough(event: any, data: any): void {
        let entryName = '';
        this.isChatBusyMessage = 'Drilling through ...';
        if (this.report.reportDisplayType === ReportDisplayType.GRID) {
            this.reportGrid.isBusy = true;
            this.reportGrid.isBusyMessage = this.isChatBusyMessage;
            entryName = this.report.reportType === ReportType.BILLING_REPORT ? data.documentClass :
                (this.report.reportType === ReportType.TECHNICAL_REDUCTION ||
                    this.report.reportType === ReportType.TECHNICAL_REDUCTION_VERTICAL) ? data.nonFormattedName :
                    this.report.reportType === ReportType.OCR_SOURCE ? data.nonFormattedNameNotProcessed : data.name;
        } else {
            if (this.chart && this.chart?.tooltip?._active && this.chart?.tooltip?._active.length > 0) {
                const activeSegment = this.chart?.tooltip?._active[0].element;
                this.isChartBusy = true;
                entryName = activeSegment.label;
            }
        }

        const drillReport = {
            reportType: this.report.reportType,
            entryName: entryName,
            viewHandle: this.handle,
            searchDialect: this.commonService.searchDialect,
            granularity: (this.report.reportType === ReportType.DATE_EMAIL_RECEIVED || this.report.reportType === ReportType.DATE_EMAIL_SENT ||
                this.report.reportType === ReportType.DATE_PRIMARY) ? this.granularity : null,
        };

        this.restService.post(Endpoint.viewManager_drillDownOnReport, drillReport).then(response => {
            if (response && response.viewHandle) {
                this.drillThroughNavigation(response);
                if (this.report.reportDisplayType === ReportDisplayType.GRID) {
                    this.isChartBusy = false;
                    this.reportGrid.isBusy = false;
                } else {
                    this.reportDrillTask(response);
                }
            } else {
                if (response.taskStatus && response.taskStatus === 'SUCCESS') {
                    const drillData = this.report.reportDisplayType === ReportDisplayType.GRID ? data : null;
                    this.drillThrough(null, drillData);
                } else {
                    this.isChartBusy = false;
                }
            }
        }, error => {
            if (this.report.reportDisplayType === ReportDisplayType.GRID) {
                this.reportGrid.isBusy = false;
            } else {
                this.isChartBusy = false;
            }
        });
    }

    reportDrillTask(responseDrill: any): void {

        const request = {
            viewHandle: responseDrill.viewHandle,
            facetFilters: null,
            dateReportRequests: [],
            reportRequests: []
        };

        request.reportRequests.push({
            cachedOnly: false,
            reportType: this.report.reportType,
            attributes: this.report.reportType === ReportType.FILETYPE ? [{ name: 'consolidated', value: 'true' }] : []
        });

        this.restService.post(Endpoint.viewManager_getReport, request).then(response => {
            if (response.taskStatus && response.taskStatus === 'SUCCESS') {
                this.reportDrillTask(responseDrill);
            } else {
                this.isChartBusy = false;
                this.drillThroughNavigation(responseDrill);
            }
        }, error => {
            if (this.report.reportDisplayType === ReportDisplayType.GRID) {
                this.reportGrid.isBusy = false;
            } else {
                this.isChartBusy = false;
            }
        });
    }

    drillThroughNavigation(response: any): void {
        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.RESULT_VIEW);
        this.commonService.sendMessage(MessageType.navigationRequest, { handle: response.viewHandle, entityType: EntityType.searchResult });
        this.commonService.sendMessage(MessageType.tabNavigationRequest, this.commonService.selectedNavigationNode.navigationInfo.tabs.filter(t => t.path === 'All Docs')[0]);
    }

    drillDown(event): void {
        if (this.chart && this.chart?.tooltip?._active && this.chart?.tooltip?._active.length > 0) {
            this.getReport(false, true);
        }
    }

    handleClick = (event) => {

        const ts = (new Date()).getTime();
        if ((ts - this.lastClick) < this.doubleClickDuration) {

            // double click
            // clear the timeout so the "click" event does not fire
            if (this.clickTimeout) {
                clearTimeout(this.clickTimeout);
            }

            // reset last click
            this.lastClick = 0;

            // do whatever we want to do on double-click
            this.drillThrough(event, null);
        } else {
            // single click
            // delay it to see if a second click will come through
            this.clickTimeout = setTimeout(() => {
                // do whatever we want to do on single click
                if (this.report.isZeroDayDetailsButtonPresent) {
                    this.drillDown(event);
                }
            }, this.doubleClickDuration);
        }
        this.lastClick = ts;
    }

    chartDataSets(type: any): any {
        let data = [];
        const dataDisplayed = { datasets: [], labels: [], labelsTrimmed: [], suggestedMax: null };
        if (this.report.reportType === this.reportTypes.DATE_EMAIL_SENT || this.report.reportType === this.reportTypes.DATE_EMAIL_RECEIVED
            || this.report.reportType === this.reportTypes.DATE_PRIMARY) {
            data = _.sortBy(this.report.data.reportEntryList, 'name');
        } else {
            const sortType = this.chartBySize ? 'size' : 'count';
            data = this.report.data.reportEntryList.sort((a, b) => b[sortType] - a[sortType]);
        }

        data.forEach((entry) => {
            if (entry && entry.name && entry.name.length > 10 && type === this.reportDisplayType.BAR_CHART) {
                entry.nameTrimmed = entry.name.substring(0, 10) + '...';
            } else {
                entry.nameTrimmed = entry.name ? entry.name : '';
            }

            dataDisplayed.labels.push(entry.name);
            dataDisplayed.labelsTrimmed.push(entry.nameTrimmed);

            if (this.chartBySize && (type === this.reportDisplayType.PIE_CHART || this.chartBySize && type === this.reportDisplayType.BAR_CHART)) {
                dataDisplayed.datasets.push(entry.size);
            } else {
                dataDisplayed.datasets.push(entry.count);
            }
        });


        const maxValue = Math.max(...data.map((o) => { if (this.chartBySize) { return o.size; } else { return o.count; } }));
        const roundMaxValue = parseInt(((5 / 100) * maxValue).toFixed(), 10);
        dataDisplayed.suggestedMax = roundMaxValue === 0 ? maxValue + 1 : roundMaxValue + maxValue;

        if (this.chart && this.chart.config && this.chart.config.data && this.chart.config.data.datasets.length > 0) {
            this.chart.config.data.datasets[0].data = dataDisplayed.datasets;
        }

        return dataDisplayed;
    }

    mouseMoveHandler = () => {
        this.showTooltips = false;  // Hide tooltips on mouse move
        this.chart.draw();          // Redraw the chart without tooltips
    };
    mouseOutHandler = () => {
        this.showTooltips = true;  // Show tooltips when the mouse is out
        this.chart.tooltip.setActiveElements([]);
        this.prevChartActiveElement = null;
        this.chart.draw();        // Redraw the chart with tooltips
    };

    renderChart(config: any, type: any): void {
        this.canvas = document.getElementById(this.uniqueId);

        this.chart = new Chart(
            this.canvas,
            config
        );

        if (type === this.reportDisplayType.PIE_CHART) {
            // Mouse out handler to show tooltips again
            this.canvas.addEventListener('mouseenter', this.mouseMoveHandler);
            this.canvas.addEventListener('mouseout', this.mouseOutHandler);
            const segments = this.chart.getDatasetMeta(0).data;
            this.formatChartTooltip(segments, this.reportDisplayType.PIE_CHART);
            this.chart.update();
        } else {
            const datasets = this.chart.getDatasetMeta(0).data;
            this.formatChartTooltip(datasets, this.reportDisplayType.BAR_CHART);
        }
    }

    getLegend(): any {
        const legend = document.getElementById(this.uniqueId + '-legend');
        return legend;
    }

    formatChartTooltip(points: any, type: any): void {
        const chartDataSets = this.chartDataSets(type);
        const datasetsLength = chartDataSets.datasets.length;
        for (let i = 0; i < points.length; i++) {
            for (let j = 0; j < datasetsLength; j++) {
                if (type === this.reportDisplayType.PIE_CHART) {
                    points[i].pieTooltipFillColor = this.shadeColor(points[i].options.backgroundColor, -30);
                    points[i].restoreFillColor = points[i].options.backgroundColor;
                    points[i].labelDisplayed = this.chart?.config?._config?.data?.labels[i];
                    points[i].label = this.chart?.config?._config?.data?.labels[i];
                } else {
                    points[i].labelDisplayed = chartDataSets.labels[j].length > 8 ? chartDataSets.labels[i].substring(0, 8) + '...' : chartDataSets.labels[i];
                    points[i].label = chartDataSets.labels[i];
                }
            }
        }
    }

    shadeColor(col: any, amt: any): string {
        let usePound = false;

        if (col[0] === '#') {
            col = col.slice(1);
            usePound = true;
        }

        let R = parseInt(col.substring(0, 2), 16);
        let G = parseInt(col.substring(2, 4), 16);
        let B = parseInt(col.substring(4, 6), 16);

        // to make the colour less bright than the input
        // change the following three "+" symbols to "-"
        R = R + amt;
        G = G + amt;
        B = B + amt;

        if (R > 255) {
            R = 255;
        } else if (R < 0) {
            R = 0;
        }

        if (G > 255) {
            G = 255;
        } else if (G < 0) {
            G = 0;
        }
        if (B > 255) {
            B = 255;
        } else if (B < 0) {
            B = 0;
        }

        const RR = ((R.toString(16).length === 1) ? '0' + R.toString(16) : R.toString(16));
        const GG = ((G.toString(16).length === 1) ? '0' + G.toString(16) : G.toString(16));
        const BB = ((B.toString(16).length === 1) ? '0' + B.toString(16) : B.toString(16));

        return (usePound ? '#' : '') + RR + GG + BB;

    }

    tabsBarVisible(): boolean {
        return this.report.headerBarTabs;
    }

    selectTab(tab: any): void {
        this.chartBySize = !tab;
        this.chart.destroy();
        this.drawChart();
    }

    compareDateRange(): void {
        if ((this.startDate && this.endDate) && this.endDate.getTime() < this.startDate.getTime()) {
            this.dateError = true;
        } else {
            this.dateError = false;
        }
        this.changeDetector.detectChanges();
    }

    applyHistogramPeriod(): void {
        this.getReportData();
    }

    resetHistogramPeriod(): void {
        this.granularity = 'YEAR';
        this.getReport(true, false);
    }

    getReportDetailsForPeriod(periodType: any): void {
        const ms = this.endDate.getTime() - this.startDate.getTime();
        const duration = moment.duration(moment(this.endDate).diff(moment(this.startDate)));
        const daysInFebMoth = new Date(this.endDate.getYear(), 1, 1);
        if (periodType === 'next') {
            if (ms > oneDayTicks) {
                const startTime = moment(this.startDate).add(duration.asDays(), 'days');
                const endTime = moment(this.endDate).add((this.startDate.getDate() === 1 && this.endDate.getDate() === 31 && this.endDate.getMonth() === 0) ?
                    moment(daysInFebMoth).daysInMonth() : duration.asDays(), 'days');

                startTime.add(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                const endTimeMonthDate = moment(endTime.creationData().input);

                if (this.granularity === 'DAY' && endTimeMonthDate.daysInMonth() === this.endDate.getDate() && this.startDate.getDate() === 1) {
                    startTime.startOf('month');
                    endTime.endOf('month');
                } else {
                    endTime.add(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                }

                this.setNewDate(startTime, endTime);
            } else {
                this.setNewDate(moment(this.startDate).add(ms === 0 ? 1 : 2, 'days'), moment(this.endDate).add(ms === 0 ? 1 : 2, 'days'));

            }
        } else {
            if (ms > oneDayTicks) {
                const startTime = moment(this.startDate).subtract((this.startDate.getDate() === 1 && this.endDate.getDate() === 31 && this.endDate.getMonth() === 2
                    && this.startDate.getMonth() === 2) ? moment(daysInFebMoth).daysInMonth() : duration.asDays(), 'days');
                const endTime = moment(this.endDate).subtract(duration.asDays(), 'days');

                endTime.subtract(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                const endTimeMonthDate = moment(endTime.creationData().input);

                if (this.granularity === 'DAY' && endTimeMonthDate.daysInMonth() === this.endDate.getDate() && this.startDate.getDate() === 1) {
                    startTime.startOf('month');
                    endTime.endOf('month');
                } else {
                    startTime.subtract(1, 'days').set('hours', 0).set('minutes', 0).set('seconds', 0);
                }

                this.setNewDate(startTime, endTime);
            } else {
                this.setNewDate(moment(this.startDate).subtract(ms === 0 ? 1 : 2, 'days'), moment(this.endDate).subtract(ms === 0 ? 1 : 2, 'days'));

            }
        }
        this.getReportData();
    }

    getReportData(): void {
        this.granularity = this.computeGranularity();
        this.getReport(false, false);
    }

    getReport(resetPeriod: any, drillDown: any): void {
        this.isChatBusyMessage = 'Loading ...';
        this.isChartBusy = true;
        const request = {
            viewHandle: this.report.node.name === 'Dashboard' ? this.commonService.projectData.handle :
                this.report.node.data && this.report.node.data.view ? this.report.node.data.view.handle : this.report.node.data.handle,
            dateReportRequests: [this.formatHistogramData(resetPeriod, drillDown)],
        };

        this.restService.post(Endpoint.viewManager_getReport, request).then(response => {
            if (response && response.reports && response.reports.length > 0) {
                this.report.data = response.reports[0];
                this.report.data.reportEntryList = _.sortBy(this.report.data.reportEntryList, 'name');
                if (resetPeriod) {
                    this.setInitialGranularity();
                } else if (drillDown) {
                    this.startDate = this.endDate = null;
                    this.setNewDate(this.report.data.reportEntryList[0].name,
                        moment(new Date(this.report.data.reportEntryList[this.report.data.reportEntryList.length - 1].name)).endOf('month'));
                }
                this.chart.destroy();
                this.drawChart();
                this.isChartBusy = false;
            } else {
                this.isChartBusy = false;
            }
        }, error => {
            this.isChartBusy = false;
        });
    }

    setNewDate(start, end): void {
        this.startDate = new Date(moment(start).format('YYYY-MM-DD') + this.time);
        this.endDate = new Date(moment(end).format('YYYY-MM-DD') + this.time);
        this.changeDetector.detectChanges();
    }

    computeGranularity(): string {
        let granularity = 'YEAR';

        if (this.startDate <= this.endDate) {
            const diff = this.dateDiffInDays(this.startDate, this.endDate);
            if (diff < 31) {
                granularity = 'DAY';
            } else if (diff >= 31 && diff < 540) {
                granularity = 'MONTH';
            }
        }
        return granularity;
    }

    formatHistogramData(resetPeriod: any, drillDown: any): any {
        let dateReportRequest = {};
        switch (this.granularity) {
            case 'DAY':
                dateReportRequest = {
                    cachedOnly: false,
                    startDate: this.formatDateByGranularity(this.startDate, drillDown, 'start'),
                    endDate: this.formatDateByGranularity(this.endDate, drillDown, 'end'),
                    granularity: this.granularity,
                    reportType: this.report.reportType,
                    sortAscending: true,
                    sortBy: 'NAME',
                };
                this.granularity = drillDown ? 'DAY' : this.granularity;
                break;
            case 'DAYS':
                dateReportRequest = {
                    cachedOnly: false,
                    reportType: this.report.reportType,
                    granularity: this.granularity,
                };
                break;
            case 'MONTH':
                dateReportRequest = {
                    cachedOnly: false,
                    reportType: this.report.reportType,
                    startDate: this.formatDateByGranularity(this.startDate, drillDown, 'start'),
                    endDate: this.formatDateByGranularity(this.endDate, drillDown, 'end'),
                    granularity: drillDown ? 'DAY' : this.granularity,
                    sortBy: 'NAME',
                    sortAscending: true,
                };
                this.granularity = drillDown ? 'DAY' : this.granularity;
                break;
            case 'YEAR':
                dateReportRequest = {
                    cachedOnly: false,
                    reportType: this.report.reportType,
                    startDate: resetPeriod ? '' : this.formatDateByGranularity(this.startDate, drillDown, 'start'),
                    endDate: resetPeriod ? '' : this.formatDateByGranularity(this.endDate, drillDown, 'end'),
                    granularity: drillDown ? 'MONTH' : this.granularity,
                    sortBy: 'NAME',
                    sortAscending: true,
                };
                this.granularity = drillDown ? 'MONTH' : this.granularity;
                break;
        }

        return dateReportRequest;
    }

    formatDateByGranularity(date: any, drillDown: any, type: string): void {
        let formattedDate;
        if (drillDown && this.chart && this.chart?.tooltip?._active && this.chart?.tooltip?._active.length > 0) {
            const barSegmentDate = this.chart.tooltip._active[0].element.label;
            const splitDate = barSegmentDate.split('-');
            const monthDate = new Date(splitDate[0], splitDate[1], 0).getDate();
            const monthStartDate = barSegmentDate.concat('-', '1');
            switch (this.granularity) {
                case 'DAY':
                    formattedDate = type === 'start' ? splitDate[0].concat('-', splitDate[1], '-', '1') :
                        splitDate[0].concat('-', splitDate[1], '-', monthDate);
                    break;
                case 'MONTH':
                    formattedDate = type === 'start' ? monthStartDate : barSegmentDate.concat('-', monthDate);
                    break;
                case 'YEAR':
                    formattedDate = type === 'start' ? barSegmentDate + '-01-01' : barSegmentDate + '-12-31';
                    break;
            }
        } else {
            switch (this.granularity) {
                case 'DAY':
                    formattedDate = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
                    break;
                case 'MONTH':
                    formattedDate = date.getFullYear() + '-' + (date.getMonth() + 1);
                    break;
                case 'YEAR':
                    formattedDate = date.getFullYear();
                    break;
            }
        }
        return formattedDate;
    }

    dateDiffInDays(a: any, b: any): number {
        const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
        const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

        return Math.floor((utc2 - utc1) / oneDayTicks);
    }

    checkTextAvailableWidth(): void {
        if (document && this.report) {
            const reportPanel = document.getElementById(this.report.automationId);
            if (reportPanel && reportPanel.clientWidth) {
                if (reportPanel) {
                    this.hiddenActionText = reportPanel.clientWidth < 500;
                }
            }
        }
    }

    labelPeriodVisible(): boolean {
        const reportPanel = document.getElementById('chart-container' + '_' + this.report.index);
        if (reportPanel?.clientWidth) {
            return reportPanel.clientWidth >= 1000;
        }
    }

    labelVisible(): boolean {
        const reportPanel = document.getElementById('chart-container' + '_' + this.report.index);
        if (reportPanel?.clientWidth) {
            return reportPanel.clientWidth >= 800;
        }
    }

    ngOnDestroy() {
        if (this.canvas && this.mouseMoveHandler) {
            this.canvas?.removeEventListener('mouseenter', this.mouseMoveHandler);
            this.canvas?.removeEventListener('mouseout', this.mouseOutHandler);
        }
        if (this.chart) {
            this.chart.destroy();
        }
    }
}
